import { createTheme } from '@mui/material/styles';
import { SHARED_COMPONENT_OVERRIDES } from '~/theme/components';
import {
    ACTION_HOVER_OVERLAY,
    ACTION_SELECTED_OVERLAY,
    DARK_RAISED_BACKGROUND,
    DARK_SUNKEN_BACKGROUND,
    TAG_CHIP_VARIANTS,
    PRIMARY_COLOR,
    PRIMARY_CONTRAST_TEXT,
    SECONDARY_COLOR,
} from '~/theme/theme';

/**
 * Always-dark theme for the imaging-console chrome (radiology convention),
 * independent of the app's light/dark toggle. Mirrors the app's dark palette
 * (brand colours from the shared, framework-free `colors.ts`) plus the design
 * reference's raised/sunken surfaces and hairline delimiter, which MUI's
 * default/paper pair can't express. Kept free of `~/theme/theme` so it never
 * pulls `@mui/icons-material` into grid unit tests.
 *
 * `ViewportGrid` provides this to its subtree, so tile chrome can wire to theme
 * tokens (`background.paper`, `divider`, `primary.main`, `background.sunken`) that
 * resolve to the reference's dark values in any app mode.
 */
declare module '@mui/material/styles' {
    interface Palette {
        chrome: {
            /** 1px vertical rule between title-bar / footer items. */
            delimiter: string;
            /** Bright keyboard focus-visible ring, readable over dark + canvas. */
            focusRing: string;
        };
    }
    interface PaletteOptions {
        chrome?: Palette['chrome'];
    }
}

/** Tabular monospace for every numeric readout (design reference: Roboto Mono). */
export const MONO_FONT =
    "'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

/** Characterful label face for titles/labels (design reference: Poppins). */
const LABEL_FONT =
    "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

export const chromeTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            light: '#4dc0ce',
            main: PRIMARY_COLOR,
            dark: '#007382',
            contrastText: PRIMARY_CONTRAST_TEXT,
        },
        secondary: {
            light: '#f6ad33',
            main: SECONDARY_COLOR,
            dark: '#c37a00',
            contrastText: '#000000',
        },
        // default = shell behind the tiles; paper = raised title/footer bars;
        // sunken = the fill of a control the user types or picks into.
        background: {
            default: '#1c262d',
            paper: DARK_RAISED_BACKGROUND,
            sunken: DARK_SUNKEN_BACKGROUND,
            raised: '#2a363d',
        },
        divider: '#333c42',
        text: { primary: '#e6edf0', secondary: '#8a97a1' },
        // Pointing at a row and choosing one look the same here as anywhere
        // else in the app; only the ground beneath them is darker.
        action: {
            hover: ACTION_HOVER_OVERLAY,
            selected: ACTION_SELECTED_OVERLAY,
        },
        chrome: {
            delimiter: '#3a464d',
            focusRing: '#4fdcff',
        },
    },
    shape: { borderRadius: 4 },
    typography: { fontFamily: LABEL_FONT },
    // A control over the console is still one of the app's controls, so it
    // reads exactly as it does anywhere else, in the console's palette.
    components: {
        ...SHARED_COMPONENT_OVERRIDES,
        MuiChip: { variants: TAG_CHIP_VARIANTS },
    },
});
